import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination as SwiperPagination, Autoplay, EffectFade } from 'swiper/modules';
import { ChevronLeft, ChevronRight, Play, Info } from 'lucide-react';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';
import styles from './CSS/AudiosByLanguage.module.css';
import { useAudiosByGenreName } from '../../../hooks/useAudiosHooks';
import Loading from '../../../common/HousGlass Loader/Loading';
import Footer from '../../../common/UI/Footer';

function AudiosByLanguage() {
  const { category } = useParams();
  const navigate = useNavigate();
  const [heroSlides, setHeroSlides] = useState([]);
  const [allAudios, setAllAudios] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12); // Default to mobile view
  const [isMobile, setIsMobile] = useState(false);

  // Detect device type and set items per page accordingly
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768; // Common breakpoint for mobile devices
      setIsMobile(mobile);
      setItemsPerPage(mobile ? 12 : 28); // 12 items on mobile, 28 on desktop
    };

    // Initial check
    handleResize();

    // Add event listener for window resize
    window.addEventListener('resize', handleResize);

    // Clean up
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch audios with the hook - increase limit to accommodate desktop view
  const { data: audiosData, isLoading, isError } = useAudiosByGenreName(
    category,
    { limit: 100, page: 1, sort_by: 'created_at', sort_order: 'DESC' },
    !!category
  );

  useEffect(() => {
    if (audiosData?.data) {
      // Filter out items where have_series is true
      const filteredAudios = audiosData.data.filter(audio => audio.have_series === false);
      
      // Set hero slides (3 featured items) from filtered data
      setHeroSlides(filteredAudios.slice(0, 3));
      
      // Set all audios data with filtered results
      setAllAudios(filteredAudios);
      
      // Calculate total pages based on current itemsPerPage value and filtered data
      const total = filteredAudios.length;
      setTotalPages(Math.ceil(total / itemsPerPage));
      
      // Reset to page 1 when itemsPerPage changes
      setCurrentPage(1);
    }
  }, [audiosData, itemsPerPage]);

  const handleAudioClick = (audioSlug) => {
    navigate(`/audio-play/${audioSlug}`);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    // Scroll to grid section
    const gridSection = document.getElementById('audio-grid');
    if (gridSection) {
      gridSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Calculate current items for pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = allAudios.slice(indexOfFirstItem, indexOfLastItem);

  if (isLoading) {
    return <div className={styles.loaderContainer}><Loading /></div>;
  }

  if (isError) {
    return <div className={styles.errorContainer}>Failed to load audios</div>;
  }

  // If no data is available
  if (!allAudios || allAudios.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.noContent}>
          No audio content available for {category}
        </div>
      </div>
    );
  }

  return (
    <>
    <div className={styles.container}>
      {/* Hero Section with Netflix-style Featured Content */}
      <section className={styles.heroSection}>
        <Swiper
          modules={[Navigation, SwiperPagination, Autoplay, EffectFade]}
          spaceBetween={0}
          slidesPerView={1}
          effect="fade"
          autoplay={{ delay: 6000, disableOnInteraction: false }}
          className={styles.heroSwiper}
        >
          {heroSlides.map((audio) => (
            <SwiperSlide key={`hero-${audio.id}`}>
              <div className={styles.heroSlide}>
                <div className={styles.heroImageContainer}>
                  <img 
                    src={audio.computer_image_url || '/assets/default-audio.jpg'} 
                    alt={audio.title}
                    className={styles.heroImage}
                  />
                  <div className={styles.heroGradient}></div>
                </div>
                <div className={styles.heroContent}>
                  <div className={styles.heroButtons}>
                    <button 
                      className={styles.playNowButton}
                      onClick={() => handleAudioClick(audio.slug)}
                    >
                      <Play size={20} />
                      Play Now
                    </button>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
        <div className={styles.heroPagination}></div>
      </section>

      {/* Title for category */}
      <h1 className={styles.languageTitle}>
        {category.charAt(0).toUpperCase() + category.slice(1)} Audio Collection
      </h1>

      {/* Grid layout for all audios */}
      <section id="audio-grid" className={styles.gridSection}>
        <div className={`${styles.gridContainer} ${isMobile ? styles.mobileGrid : styles.desktopGrid}`}>
          {currentItems.map((audio) => (
            <div 
              key={`grid-${audio.id}`}
              className={styles.gridItem}
              onClick={() => handleAudioClick(audio.slug)}
            >
              <div className={styles.gridImageContainer}>
                <img 
                  src={audio.mobile_image_url || '/assets/default-audio.jpg'} 
                  alt={audio.title}
                  className={styles.gridItemImage}
                />
             
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Pagination controls */}
      {totalPages > 1 && (
        <div className={styles.paginationControls}>
          <button 
            className={`${styles.paginationButton} ${currentPage === 1 ? styles.disabled : ''}`}
            onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            <ChevronLeft size={18} />
            Previous
          </button>
          
          <div className={styles.pageNumbers}>
            {[...Array(totalPages)].map((_, index) => {
              const pageNumber = index + 1;
              // Show first page, last page, current page, and 1 page before/after current
              if (
                pageNumber === 1 ||
                pageNumber === totalPages ||
                pageNumber === currentPage ||
                pageNumber === currentPage - 1 ||
                pageNumber === currentPage + 1
              ) {
                return (
                  <button
                    key={`page-${pageNumber}`}
                    className={`${styles.pageNumber} ${currentPage === pageNumber ? styles.activePage : ''}`}
                    onClick={() => handlePageChange(pageNumber)}
                  >
                    {pageNumber}
                  </button>
                );
              } else if (
                (pageNumber === currentPage - 2 && currentPage > 3) ||
                (pageNumber === currentPage + 2 && currentPage < totalPages - 2)
              ) {
                return <span key={`ellipsis-${pageNumber}`} className={styles.ellipsis}>...</span>;
              }
              return null;
            })}
          </div>
          
          <button 
            className={`${styles.paginationButton} ${currentPage === totalPages ? styles.disabled : ''}`}
            onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
            <ChevronRight size={18} />
          </button>
        </div>
      )}
    </div>
    <Footer/>
    </>
  );
}

export default AudiosByLanguage;